import { useCallback, useEffect, useRef, useState } from 'react'
import { buildVoiceNavUrl } from '../api/apiConfig'
import type { InboxReadAloudSegment } from '../utils/extractInboxReadAloud'

export type ReadAloudPhase = 'idle' | 'loading' | 'playing' | 'paused'

type TtsResponse = {
  format: 'mp3'
  parts: string[]
}

function revokeAll(urls: string[]) {
  urls.forEach((u) => {
    try {
      URL.revokeObjectURL(u)
    } catch {
      // ignore
    }
  })
}

/**
 * AWS Polly playback: sequential MP3 parts, Play / Pause / Stop (no browser SpeechSynthesis).
 */
export function useReadAloudPolly(apiBaseUrl: string) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const queueRef = useRef<string[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const [phase, setPhase] = useState<ReadAloudPhase>('idle')
  const [error, setError] = useState<string | null>(null)
  /** After successful synthesis; cleared on Stop. Used to enable Play after natural end. */
  const [canReplay, setCanReplay] = useState(false)

  const clearAudioElement = useCallback(() => {
    const a = audioRef.current
    if (!a) return
    a.pause()
    a.onended = null
    a.currentTime = 0
    a.removeAttribute('src')
    try {
      a.load()
    } catch {
      // ignore
    }
  }, [])

  const revokeQueue = useCallback(() => {
    revokeAll(queueRef.current)
    queueRef.current = []
  }, [])

  const playFromIndex = useCallback(
    (startIndex: number) => {
      const a = audioRef.current
      const q = queueRef.current
      if (!a || startIndex < 0 || startIndex >= q.length) {
        setPhase('idle')
        return
      }

      const playIdx = (i: number) => {
        if (i >= q.length) {
          setPhase('idle')
          return
        }
        a.src = q[i]
        a.onended = () => {
          if (i < q.length - 1) {
            playIdx(i + 1)
          } else {
            setPhase('idle')
            setCanReplay(true)
          }
        }
        void a.play().catch(() => {
          setError('Playback was blocked or failed.')
          setPhase('idle')
        })
        setPhase('playing')
      }

      playIdx(startIndex)
    },
    [],
  )

  const play = useCallback(() => {
    const a = audioRef.current
    if (!a) return
    if (phase === 'paused') {
      void a.play().then(() => setPhase('playing')).catch(() => setError('Playback failed.'))
      return
    }
    if (queueRef.current.length === 0) return
    playFromIndex(0)
  }, [phase, playFromIndex])

  const pause = useCallback(() => {
    audioRef.current?.pause()
    setPhase('paused')
  }, [])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    clearAudioElement()
    revokeQueue()
    setPhase('idle')
    setError(null)
    setCanReplay(false)
  }, [clearAudioElement, revokeQueue])

  const playMp3Base64Parts = useCallback(
    (partsB64: string[]) => {
      abortRef.current?.abort()
      abortRef.current = null
      clearAudioElement()
      revokeQueue()
      setError(null)
      setCanReplay(false)

      if (partsB64.length === 0) {
        setError('No audio to play.')
        return
      }

      try {
        const urls: string[] = []
        for (const b64 of partsB64) {
          const bin = atob(b64)
          const bytes = new Uint8Array(bin.length)
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
          const blob = new Blob([bytes], { type: 'audio/mpeg' })
          urls.push(URL.createObjectURL(blob))
        }
        queueRef.current = urls
        setCanReplay(true)
        // Mount <audio> via ReadAloudBar before play (phase was idle → bar hidden).
        setPhase('loading')
        requestAnimationFrame(() => playFromIndex(0))
      } catch {
        setError('Playback failed.')
        setPhase('idle')
      }
    },
    [clearAudioElement, revokeQueue, playFromIndex],
  )

  const synthesizeAndPlay = useCallback(
    async (segments: InboxReadAloudSegment[]) => {
      abortRef.current?.abort()
      abortRef.current = null
      clearAudioElement()
      revokeQueue()
      setError(null)
      setCanReplay(false)

      if (segments.length === 0) {
        setError('No message text found to read.')
        return
      }

      const ac = new AbortController()
      abortRef.current = ac
      setPhase('loading')

      try {
        const res = await fetch(buildVoiceNavUrl(apiBaseUrl, 'ttsSynthesize'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            segments: segments.map((s) => ({ text: s.text, language: s.language })),
          }),
          signal: ac.signal,
        })
        if (!res.ok) {
          const detail = await res.text().catch(() => '')
          throw new Error(detail || `HTTP ${res.status}`)
        }
        const data = (await res.json()) as TtsResponse
        const parts = data.parts || []
        if (parts.length === 0) throw new Error('Empty audio response')

        const urls: string[] = []
        for (const b64 of parts) {
          const bin = atob(b64)
          const bytes = new Uint8Array(bin.length)
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
          const blob = new Blob([bytes], { type: 'audio/mpeg' })
          urls.push(URL.createObjectURL(blob))
        }
        queueRef.current = urls
        setCanReplay(true)
        playFromIndex(0)
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          setPhase('idle')
          return
        }
        setError(e instanceof Error ? e.message : 'Speech synthesis failed.')
        setPhase('idle')
      } finally {
        if (abortRef.current === ac) abortRef.current = null
      }
    },
    [apiBaseUrl, clearAudioElement, revokeQueue, playFromIndex],
  )

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      clearAudioElement()
      revokeQueue()
    }
  }, [clearAudioElement, revokeQueue])

  return {
    audioRef,
    phase,
    error,
    canReplay,
    play,
    pause,
    stop,
    synthesizeAndPlay,
    playMp3Base64Parts,
  }
}
